import { renderReportIssueToolUse } from '@heidi/agents';
import type { ToolUseItem } from '@heidi/agents/contracts';
import { resolveToolPresentation, type ToolCardKind } from '@heidi/agents/data';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import { renderClinicalTaskToolUse } from './clinical-task';
import { renderCoverageEligibilityToolUse } from './coverage-eligibility';
import { renderCreatePatientEvidencePdfToolUse } from './create-patient-evidence-pdf';
import { renderCreateTemplateToolUse } from './create-template';
import { renderExportToPdfToolUse } from './export-to-pdf';
import { renderExportToWordToolUse } from './export-to-word';
import { renderFillPdfFormToolUse } from './fill-pdf-form';
import { renderGenerateCsvToolUse } from './generate-csv';
import { renderGenerateExcelToolUse } from './generate-excel';
import { renderGeneratePptxToolUse } from './generate-pptx';
import { renderGenerateWordToolUse } from './generate-word';
import { renderMedicalCalculatorToolUse } from './medical-calculator';
import { renderMultiplayerHandoffToolUse } from './multiplayer';
import {
  renderOpenTemplateEditorToolUse,
  renderPinTemplateToolUse,
  renderUnpinTemplateToolUse,
} from './open-template-editor';
import { renderRecordNotePushHintsToolUse } from './record-note-push-hints';
import { renderSessionRecordingToolUse } from './session-recording';
import { renderSaveWorkOutputToolUse } from './shared/artefact-tool';
import type { OpenExportDocumentPayload } from './shared/exportDocumentPreview';
import { renderUpdateWordLibraryToolUse } from './update-word-library';

import type { Artifact } from '@heidi/agents/data';

type EvidenceCardRenderer = (
  item: ToolUseItem,
  intl: IntlShape,
  options?: RenderEvidenceToolUseOptions
) => ReactNode;

const EVIDENCE_CARD_RENDERERS: Partial<Record<ToolCardKind, EvidenceCardRenderer>> = {
  'export-pdf': (item, intl, options) =>
    renderExportToPdfToolUse(item, intl, options?.onOpenDocumentExport),
  'export-csv': (item, intl, options) =>
    renderGenerateCsvToolUse(item, intl, options?.onOpenDocumentExport),
  'export-excel': (item, intl, options) =>
    renderGenerateExcelToolUse(item, intl, options?.onOpenDocumentExport),
  'export-pptx': (item, intl, options) =>
    renderGeneratePptxToolUse(
      item,
      intl,
      options?.onOpenPresentation,
      options?.onOpenDocumentExport
    ),
  'export-word': (item, intl, options) =>
    renderGenerateWordToolUse(item, intl, options?.onOpenDocumentExport),
  'export-to-word': (item, intl, options) =>
    renderExportToWordToolUse(item, intl, options?.onOpenDocumentExport),
  'patient-evidence-pdf': (item, intl, options) =>
    renderCreatePatientEvidencePdfToolUse(item, intl, options?.onOpenDocumentExport),
  'fill-pdf-form': (item, intl, options) =>
    renderFillPdfFormToolUse(item, intl, options?.onOpenDocumentExport),
  'coverage-eligibility': (item, _intl, options) =>
    renderCoverageEligibilityToolUse(item, options?.onOpenArtifact),
  'multiplayer-handoff': (item, _intl, options) =>
    renderMultiplayerHandoffToolUse(item, options?.onOpenSession),
  'clinical-task': (item) => renderClinicalTaskToolUse(item),
  'medical-calculator': (item) => renderMedicalCalculatorToolUse(item),
  'create-template': (item) => renderCreateTemplateToolUse(item),
  'open-template-editor': (item) => renderOpenTemplateEditorToolUse(item),
  'pin-template': (item) => renderPinTemplateToolUse(item),
  'unpin-template': (item) => renderUnpinTemplateToolUse(item),
  'save-work-output': (item, intl, options) =>
    renderSaveWorkOutputToolUse(item, intl, options?.onOpenArtifact),
  'record-note-push-hints': (item, intl) => renderRecordNotePushHintsToolUse(item, intl),
  'report-issue': (item) => renderReportIssueToolUse(item),
  'update-word-library': (item) => renderUpdateWordLibraryToolUse(item),
  'session-recording': (item) => renderSessionRecordingToolUse(item),
};

/** Does this tool render through a bespoke Evidence adapter (not the generic row)? */
export const isEvidenceAdapterToolName = (name: string): boolean => {
  const kind = resolveToolPresentation({ name }).card;
  return kind !== undefined && EVIDENCE_CARD_RENDERERS[kind] !== undefined;
};

export interface RenderEvidenceToolUseOptions {
  onOpenSession?: (sessionId: string) => void;
  /**
   * Opens the Slide Builder artifact panel for a generated presentation.
   * Only surfaces that mount the artifact canvas pass this; without it a
   * pptx card falls back to downloading the export.
   */
  onOpenPresentation?: (presentationId: string) => void;
  /**
   * Opens a downloadable export in the artifact canvas (filled forms, PDF,
   * Word, Excel, …). Without it, cards fall back to opening the URL in a tab.
   */
  onOpenDocumentExport?: (payload: OpenExportDocumentPayload) => void;
  /** Opens calendar / mail (and other) snapshot artifacts in the canvas. */
  onOpenArtifact?: (artifact: Artifact) => void;
}

export const renderEvidenceToolUse = (
  item: ToolUseItem,
  intl: IntlShape,
  options?: RenderEvidenceToolUseOptions
): ReactNode | null => {
  const kind = resolveToolPresentation(item).card;
  if (!kind) return null;
  return EVIDENCE_CARD_RENDERERS[kind]?.(item, intl, options) ?? null;
};
